// Static demo-mode fill-in data for UserAttendance.component.jsx.
//
// Unlike Timeline.demoData.js / TeamDashboard.demoData.js (which are plain
// hardcoded constants tied to a fixed date), this view renders a full month
// calendar (MonthCalendarGrid) driven by `currentMonth` state that the user
// can page forward/back via the month DatePicker and prev/next arrows. A
// fixed set of hardcoded dates would only populate one specific month and
// leave every other month looking empty when the demo user navigates the
// picker. So `buildDemoTimeLogInfo` generates a full month's worth of
// `timeLogInfo` entries (shape: `{ Status, date (YYYY-MM-DD), color, key }`,
// matching `classifyDayStatus`'s output in UserAttendance.component.jsx) for
// *whichever* month is passed in, using a deterministic weekday/weekend
// pattern plus a couple of fixed leave/holiday days — so switching months
// always shows a fully-populated, realistic calendar instead of a blank one.

import moment from 'moment-timezone';
import {
  userLeaveEnum,
  userLeaveLabelEnum,
  userLeaveColorConfig,
} from './UserAttendance.constants';

// Consumed by the member Dropdown ({ value, label }) — reuses the same demo
// employee names as Timeline.demoData.js / TeamDashboard.demoData.js where
// possible for consistency across the demo, plus two extra names since this
// view's dropdown supports a slightly larger roster.
export const DEMO_MEMBERS_OPTIONS = [
  { value: 'demo-user', label: 'Ryan Mitchell' },
  { value: 'demo-user-2', label: 'Rachel Turner' },
  { value: 'demo-user-3', label: 'Michael Chen' },
  { value: 'demo-user-4', label: 'Sarah Johnson' },
  { value: 'demo-user-5', label: 'David Kim' },
];

// Fixed day-of-month overrides applied on top of the weekday/weekend
// pattern below — gives every demo month one sick day, one casual leave day,
// and one company holiday, regardless of which month is being viewed.
const DAY_OF_MONTH_OVERRIDES = {
  10: {
    label: userLeaveLabelEnum.sickLeave,
    key: userLeaveEnum.sickLeave,
  },
  18: {
    label: userLeaveLabelEnum.casualLeave,
    key: userLeaveEnum.casualLeave,
  },
  22: {
    label: 'Founders Day',
    key: userLeaveEnum.holiday,
  },
};

// Consumed by `timeLogByDate`/`getDayContent` (calendar pills) and, via
// `calculatePresentAndLeave`, by the stat cards, donut breakdown, and
// working-days/attendance-% tiles. Generates a mostly-present month with
// weekends as "Week Off", plus the fixed leave/holiday days above — for
// *any* month/year passed in, so paging the month picker never empties the
// calendar or its derived stats.
export const buildDemoTimeLogInfo = (monthDate) => {
  const start = moment(monthDate).startOf('month');
  const daysInMonth = start.daysInMonth();
  const result = [];

  for (let day = 1; day <= daysInMonth; day++) {
    const current = start.clone().date(day);
    const dayOfWeek = current.day(); // 0 = Sun, 6 = Sat
    const override = DAY_OF_MONTH_OVERRIDES[day];

    let statusKey;
    let statusLabel;
    if (override) {
      statusKey = override.key;
      statusLabel = override.label;
    } else if (dayOfWeek === 0 || dayOfWeek === 6) {
      statusKey = userLeaveEnum.weekOff;
      statusLabel = userLeaveLabelEnum.weekOff;
    } else {
      statusKey = userLeaveEnum.present;
      statusLabel = userLeaveLabelEnum.present;
    }

    result.push({
      Status: statusLabel,
      date: current.format('YYYY-MM-DD'),
      color: userLeaveColorConfig[statusKey],
      key: statusKey,
    });
  }

  return result;
};

// Consumed by the "vs last month" trend badges on the stat cards
// (`trendByKey`) — a fixed prior-month snapshot to diff the current demo
// month's counts against, so the trend arrows always render sensible values.
export const DEMO_PREVIOUS_MONTH_COUNTS = {
  present: 20,
  absent: 0,
  weekOff: 8,
  holiday: 1,
  sickLeave: 2,
  casualLeave: 0,
  plannedLeave: 0,
  unpaidLeave: 0,
};
